import { Box } from '@chakra-ui/react'
import {
  RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

export interface HeightSynchronizerProps {
  children?: React.ReactNode
}

export interface HeightSyncWRapperProps {
  /**
   * Provide a group name to sync heights across multiple components using the smae group.
   */
  group: string
  children?: React.ReactNode
  rootRef?: any
}

export const heightSyncContext2 = createContext({
  groups: [] as HeightGroup2[],
  setRef: (() => {}) as (inputRef: SyncRef, groupId: string) => void,
  removeRef: (() => {}) as (id: string, groupId: string) => void,
})

type GroupRef = {
  groupId: string
  refs: SyncRef[]
}

type SyncRef = {
  id: string
  ref: RefObject<HTMLDivElement | null>
}

type HeightGroup2 = {
  group: string
  height: number | undefined
}
export const HeightSynchronizer = ({ children }: HeightSynchronizerProps) => {
  const refs = useRef<GroupRef[]>([])
  const [heightGroups, setHeightGroups] = useState<HeightGroup2[]>([])

  const setRef = useCallback((inputRef: SyncRef, groupId: string) => {
    const hasGroup = refs.current.find(group => group.groupId === groupId)

    if (!hasGroup) {
      refs.current.push({
        groupId,
        refs: [],
      })
    }

    refs.current = refs.current.map(group => {
      if (group.groupId !== groupId) {
        return group
      }

      const newGroup = {
        ...group,
      }

      const hasRef = newGroup.refs.find(ref => ref.id === inputRef.id)

      if (!hasRef) {
        newGroup.refs.push(inputRef)
      } else {
        newGroup.refs = newGroup.refs.map(ref => {
          if (ref.id !== inputRef.id) {
            return ref
          }
          return inputRef
        })
      }

      const heights = newGroup.refs.map(ref => {
        return ref.ref.current?.offsetHeight ?? 0
      })

      setHeightGroups(groups => {
        const newGroups = groups.filter(group => group.group !== groupId)
        newGroups.push({
          group: groupId,
          height: Math.max(...heights),
        })
        return newGroups
      })

      return newGroup
    })
  }, [])

  const removeRef = useCallback((id: string, groupId: string) => {
    refs.current = refs.current.map(group => {
      if (group.groupId !== groupId) {
        return group
      }

      const newGroup = {
        ...group,
        refs: group.refs.filter(ref => ref.id !== id),
      }

      const heights = newGroup.refs.map(ref => {
        return ref.ref.current?.offsetHeight ?? 0
      })

      setHeightGroups(groups => {
        const newGroups = groups.filter(group => group.group !== groupId)
        newGroups.push({
          group: groupId,
          height: Math.max(...heights),
        })
        return newGroups
      })

      return newGroup
    })
  }, [])

  return (
    <heightSyncContext2.Provider
      value={{ groups: heightGroups, setRef, removeRef }}
    >
      {children}
    </heightSyncContext2.Provider>
  )
}

export const HeightSyncWrapper = ({
  children,
  group,
  rootRef,
}: HeightSyncWRapperProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const id = useId()

  const { groups, removeRef, setRef } = useContext(heightSyncContext2)
  const groupName = useRef(group)

  const height = groups.find(
    currentGroup => currentGroup.group === groupName.current,
  )?.height

  useEffect(() => {
    if (!ref.current) return
    groupName.current = group + rootRef.current.offsetTop

    setRef({ id, ref }, groupName.current)

    const observer = new ResizeObserver(mutations => {
      groupName.current = group + rootRef.current.offsetTop
      mutations.forEach(() => {
        setRef({ id, ref }, groupName.current)
      })
    })

    const observer2 = new MutationObserver(mutations => {
      groupName.current = group + rootRef.current.offsetTop

      mutations.forEach(() => {
        setRef({ id, ref }, groupName.current)
      })
    })

    observer.observe(ref.current)
    observer2.observe(ref.current, {
      attributes: true,
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      observer2.disconnect()
      removeRef(id, groupName.current)
    }
  }, [])

  return (
    // TODO: maybe force horizontal stretch too.
    // Use minHeight (not height) so synced rows never clip content when the
    // measured max is briefly stale or line-height / font metrics differ.
    <Box style={{ minHeight: height }} data-component="HeightSyncWrapper">
      <Box ref={ref} data-component="HeightSyncWrapperContent">
        {children}
      </Box>
    </Box>
  )
}
